import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Flex, Typography } from 'antd';
import BackButton from '../../components/BackButton';
import OnboardingShell from '../../components/OnboardingShell';
import { useCard } from '../../context/CardContext';
import { useOnboarding } from '../../context/OnboardingContext';
import CardForm from '../add-card/CardForm';
import VerifyForm from '../add-card/VerifyForm';
import { buildBotPath } from '../../utils/routes';
import { formatCurrency, formatSubscriptionType, formatTariffDuration } from '../../utils/format';
import { Navigate } from 'react-router-dom';

interface PaymentPageProps {
    replaceMode?: boolean;
}

const PaymentPage = ({ replaceMode = false }: PaymentPageProps) => {
    const [step, setStep] = useState(CardForm.name);
    const navigate = useNavigate();
    const { bot_id } = useParams();
    const { subscriptionInfo, subscriptionStatus, waitForActiveSubscription, refreshStatus } = useOnboarding();
    const { selectedTariff } = useCard();

    const handleSuccess = async () => {
        if (replaceMode) {
            // Nothing was charged — the next scheduled payment will bill the new card. Just refresh
            // the status (updates `activeCard`) without the fullscreen wait spinner and return.
            await refreshStatus().catch(() => undefined);
            navigate(buildBotPath(bot_id, '/onboarding/dashboard'));
            return;
        }

        // Activation just queues a payment job on the backend, so the subscription isn't active
        // yet at this point — wait for it instead of reading a stale status.
        const paymentOutcome = await waitForActiveSubscription().catch(() => 'pending' as const);
        navigate(buildBotPath(bot_id, '/onboarding/success'), { state: { paymentOutcome } });
    };

    const renderStep = () => {
        if (replaceMode) {
            // Card swap only makes sense with an active subscription. Without one, fall back to the
            // regular first-payment flow via the dashboard.
            if (subscriptionStatus && !subscriptionStatus.is_active) {
                return <Navigate to={buildBotPath(bot_id, '/onboarding/dashboard')} replace />;
            }
        } else if (!selectedTariff) {
            return <Navigate to={buildBotPath(bot_id, '/onboarding/tariff')} replace />;
        }
        switch (step) {
            case CardForm.name:
                return <CardForm setStep={setStep} />;
            case VerifyForm.name:
                return <VerifyForm onSuccess={handleSuccess} replaceMode={replaceMode} />;
            default:
                return <CardForm setStep={setStep} />;
        }
    };

    return (
        <OnboardingShell
            title={replaceMode ? 'Kartani almashtirish' : 'Kartani ulash'}
            subtitle={
                replaceMode
                    ? "Yangi kartani qo'shing va tasdiqlang. Keyingi to'lov yangi kartadan yechib olinadi."
                    : "To'lovni davom ettirish uchun kartani qo'shing va tasdiqlang."
            }
            backButton={
                <BackButton
                    onClick={() =>
                        step === CardForm.name
                            ? navigate(buildBotPath(bot_id, replaceMode ? '/onboarding/dashboard' : '/onboarding/offer'))
                            : setStep(CardForm.name)
                    }
                />
            }
        >
            <Flex vertical gap={16}>
                {!replaceMode && (
                    <Card className="info-panel" bordered={false}>
                        <Typography.Title level={5} className="section-title">
                            To'lov ma'lumotlari
                        </Typography.Title>
                        <Flex vertical gap={4}>
                            <Typography.Text className="section-text">
                                {selectedTariff?.title || subscriptionInfo?.tariffs?.[0]?.title || 'RiMafia Premium'} ·{' '}
                                {selectedTariff
                                    ? formatTariffDuration(selectedTariff.subscription_type, selectedTariff.period_count, selectedTariff.period_seconds)
                                    : subscriptionInfo?.tariffs?.[0]
                                        ? formatTariffDuration(subscriptionInfo.tariffs[0].subscription_type, subscriptionInfo.tariffs[0].period_count, subscriptionInfo.tariffs[0].period_seconds)
                                        : formatSubscriptionType('monthly')}
                            </Typography.Text>
                            <Flex vertical>
                                <Typography.Text className="price-title">
                                    {selectedTariff
                                        ? formatCurrency(selectedTariff.amount, selectedTariff.currency)
                                        : subscriptionInfo?.tariffs?.[0]
                                            ? formatCurrency(subscriptionInfo.tariffs[0].amount, subscriptionInfo.tariffs[0].currency)
                                            : '39 000 UZS'}
                                </Typography.Text>
                                {/* Only says so when there actually is a discount — `selectedTariff` is
                                    null on the fallback branch above, and an undiscounted tariff has
                                    nothing to explain. */}
                                {selectedTariff?.discount && (
                                    <Typography.Text className="section-text">
                                        * Chegirma faqat birinchi to'lovga tatbiq etiladi. Keyingi to'lovlar standart ({formatCurrency(selectedTariff.list_amount, selectedTariff.currency)}) tarif bo'yicha olinadi.
                                    </Typography.Text>
                                )}
                            </Flex>
                        </Flex>
                    </Card>
                )}
                {renderStep()}
            </Flex>
        </OnboardingShell>
    );
};

export default PaymentPage;
